import { SubscriptionIntervals, SubscriptionPlans } from "@prisma/client";
import clsx from "clsx";

import Icon, { Icons } from "@musicfy/components/Icon";
import StartCheckoutButton from "@musicfy/components/PaymentButtons/StartCheckoutButton";
import {
  getDailyFairUseLabel,
  getSongQuotaLabel,
} from "@musicfy/contants/Quotas";
import {
  SALE_BADGE_LABEL,
  SALE_CARD_DEADLINE_SUBTEXT,
} from "@musicfy/contants/Sale";
import {
  type AvailablePlans,
  PRICE_ID_TO_PRICE_MAP,
  PriceId,
  type TBasePlanInfo,
  type TPriceId,
  getFormattedPrice,
  getSubscriptionPriceDetails,
} from "@musicfy/contants/Subscriptions";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import {
  formatPrice,
  parsePaddleAmount,
  resolvePaddleCurrencyCode,
} from "@musicfy/utils";

interface IPlanCardProps {
  interval: SubscriptionIntervals;
  details: TBasePlanInfo;
}

type Feature = { text: string; v2?: boolean };

/**
 * Drop the trailing ".00" so whole-dollar prices read "$4" instead of
 * "$4.00", while leaving fractional prices like "$24.99" untouched.
 * Handles common locale formats (decimal point or decimal comma).
 */
const trimWholeUnit = (formatted: string): string =>
  formatted.replace(/[.,]00(?=\D*$)/, "");

/**
 * "Was" prices for the sale strikethrough comparison.
 * Marketing-chosen anchors that show 60% off the live Paddle prices.
 * Keep in sync with Checkout.tsx.
 */
const MEMORIAL_YEARLY_STRIKETHROUGH: Partial<Record<AvailablePlans, number>> = {
  starter: 190,
  pro: 590,
  studio: 1200,
};
const MEMORIAL_MONTHLY_STRIKETHROUGH: Partial<Record<AvailablePlans, number>> =
  {
    starter: 24.99,
    pro: 62.49,
    studio: 174.99,
  };

const PLAN_FEATURES: Record<AvailablePlans, Feature[]> = {
  starter: [
    { text: getDailyFairUseLabel(SubscriptionPlans.starter) },
    { text: getSongQuotaLabel(SubscriptionPlans.starter) },
    { text: "All Musicfy & Community Voices" },
    { text: "25 MB Upload Limit" },
    { text: "Master quality output", v2: true },
    { text: "Multiple variations per gen", v2: true },
  ],
  pro: [
    { text: getDailyFairUseLabel(SubscriptionPlans.pro) },
    { text: getSongQuotaLabel(SubscriptionPlans.pro) },
    { text: "Fastest Speed · 100 MB Uploads" },
    { text: "Commercial License" },
    { text: "Studio-grade audio fidelity", v2: true },
    { text: "Multiple variations per gen", v2: true },
    { text: "Bigger uploads, longer tracks", v2: true },
  ],
  studio: [
    { text: getDailyFairUseLabel(SubscriptionPlans.studio) },
    { text: getSongQuotaLabel(SubscriptionPlans.studio) },
    { text: "150 MB Uploads · 1-hr datasets · Simultaneous Training" },
    { text: "Commercial License" },
    { text: "Native studio environment", v2: true },
    { text: "Generate from your audio", v2: true },
  ],
  // `label` plan is hidden from MemorialDayPricing (see index.tsx filter),
  // but the type system needs an entry here.
  label: [],
};

/**
 * Plan card with Summer Sale chrome (badge, strikethrough, save line).
 * WELCOME50 / founding CTAs stay email-deep-link isolated.
 */
const MemorialDayPlanCard = ({
  details,
  interval,
}: IPlanCardProps): JSX.Element => {
  const {
    pricesContext: { pricesData },
  } = usePaddleContext();

  const monthlyPriceId: TPriceId =
    PriceId[`${details.plan}_${SubscriptionIntervals.month}`];
  const yearlyPriceId: TPriceId =
    PriceId[`${details.plan}_${SubscriptionIntervals.year}`];

  const priceId: TPriceId = PriceId[`${details.plan}_${interval}`];
  const priceDetails = pricesData?.details.lineItems.find(({ price }) => {
    return price.id === priceId;
  });

  const selectedPrice = priceDetails
    ? parsePaddleAmount(
        priceDetails.totals.total,
        resolvePaddleCurrencyCode(!!pricesData, pricesData?.currencyCode)
      )
    : priceId
      ? PRICE_ID_TO_PRICE_MAP[priceId] / 100
      : 0;

  const {
    price: monthlyBillingFormatted,
    floatPrice: monthlyBillingFloat,
    currencyCode,
  } = getFormattedPrice(
    monthlyPriceId,
    pricesData,
    SubscriptionIntervals.month
  );

  const { price: yearlyPerMonthFormatted } = getFormattedPrice(
    yearlyPriceId,
    pricesData,
    SubscriptionIntervals.month
  );
  const { price: yearlyTotalFormatted, floatPrice: yearlyTotalFloat } =
    getFormattedPrice(yearlyPriceId, pricesData, SubscriptionIntervals.year);

  const isYearly = interval === SubscriptionIntervals.year;

  const activeMonthlyFormatted = trimWholeUnit(
    isYearly ? yearlyPerMonthFormatted : monthlyBillingFormatted
  );
  const trimmedYearlyTotalFormatted = trimWholeUnit(yearlyTotalFormatted);

  const yearlyStrikethroughFloat =
    MEMORIAL_YEARLY_STRIKETHROUGH[details.plan] ?? 0;
  const monthlyStrikethroughFloat =
    MEMORIAL_MONTHLY_STRIKETHROUGH[details.plan] ?? 0;

  const yearlySavings = Math.max(
    yearlyStrikethroughFloat - yearlyTotalFloat,
    0
  );
  const monthlySavings = Math.max(
    monthlyStrikethroughFloat - monthlyBillingFloat,
    0
  );

  const activeStrikethroughFloat = isYearly
    ? yearlyStrikethroughFloat
    : monthlyStrikethroughFloat;
  const activeStrikethroughFormatted = trimWholeUnit(
    formatPrice(activeStrikethroughFloat, currencyCode)
  );
  const strikethroughUnit = isYearly ? "/year" : "/month";

  const activeSavings = isYearly ? yearlySavings : monthlySavings;
  const formattedActiveSavings = trimWholeUnit(
    formatPrice(activeSavings, currencyCode)
  );
  const savingsUnit = isYearly ? "/year" : "/month";

  const { plan: pricePlan } = getSubscriptionPriceDetails({
    plan: details.plan,
    interval: interval,
  });

  const iconMap: {
    [key in AvailablePlans]: Icons;
  } = {
    pro: Icons.pricingCrown,
    starter: Icons.pricingBolt,
    studio: Icons.pricingTrophy,
    label: Icons.pricingTrophy,
  };

  const iconName = iconMap[details.plan];
  const isPopular = pricePlan === SubscriptionPlans.pro;
  const planFeatures: Feature[] = PLAN_FEATURES[details.plan] ?? [];

  const showSavingsTreatment = activeSavings > 0;

  return (
    <div
      className={clsx(
        "bg-gradient-to-b relative from-navy/30 to-navy border flex flex-col rounded-12 border-solid pb-5 drop-shadow-base w-full p-4",
        {
          "border-pink-200/60 backdrop-blur-3xl": isPopular,
          "border-white/20": !isPopular,
        }
      )}
    >
      {isPopular && (
        <div className="py-1 px-4 rounded-full text-12 text-pink-200 border border-pink-200 bg-[#160E23] max-w-max absolute right-0 left-0 mx-auto top-0 -translate-y-1/2">
          Most Popular
        </div>
      )}

      {/* Header */}
      <div className="rounded-8 border border-white/[10%] p-4 bg-neutral-800/50">
        <div className="flex gap-3 items-center justify-between">
          <div className="min-w-0">
            <div className="text-24 font-bold">{details.label}</div>
            <div
              className={clsx(
                "mt-1 text-14 font-semibold text-transparent bg-clip-text bg-gradient-to-r",
                {
                  "from-pink-200 to-pink-400": isPopular,
                  "from-white/70 to-white": !isPopular,
                }
              )}
            >
              {details.benefits}
            </div>
          </div>
          <Icon name={iconName} className="flex-shrink-0" />
        </div>
      </div>

      {/* Price */}
      <div className="px-3 mt-4">
        {showSavingsTreatment && (
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1 rounded-full border border-white/30 bg-gradient-to-r from-red-dark via-red to-blue px-2 py-0.5 text-10 font-bold uppercase tracking-wider text-white">
              <span>{SALE_BADGE_LABEL}</span>
            </div>
            <div className="text-11 italic text-white/70">
              {SALE_CARD_DEADLINE_SUBTEXT}
            </div>
          </div>
        )}

        {showSavingsTreatment && (
          <div className="mt-2 text-14 text-white/40 line-through">
            {activeStrikethroughFormatted}
            {strikethroughUnit}
          </div>
        )}
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className="text-38 font-bold leading-none">
            {activeMonthlyFormatted}
          </span>
          <span className="text-16 text-pink-200">/month</span>
        </div>
        <div className="mt-1 text-14 text-white/70">
          {selectedPrice === 0
            ? "Free"
            : isYearly
              ? `Billed annually · ${trimmedYearlyTotalFormatted}/year`
              : "Billed monthly"}
        </div>
        {showSavingsTreatment && (
          <div className="mt-1 text-14 font-semibold text-pink-200">
            Save {formattedActiveSavings}
            {savingsUnit} · locked for life
          </div>
        )}
      </div>

      {/* Features */}
      <div className="my-4 h-px w-full bg-white/20" />
      <div className="px-2 flex flex-grow flex-col gap-2 text-14">
        {planFeatures.map(({ text, v2 }) => (
          <div className="flex items-center gap-2" key={text}>
            <Icon name="check" className="flex-shrink-0" />
            <span className="flex-1">{text}</span>
            {v2 && (
              <span className="flex-shrink-0 rounded-full border border-pink-200/60 bg-pink-200/10 px-2 py-0 text-10 font-bold uppercase tracking-wider text-pink-200">
                Musicfy v2
              </span>
            )}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-5 w-full px-2">
        <StartCheckoutButton
          priceId={priceId}
          cta={`Lock ${details.label} — ${activeMonthlyFormatted}/month`}
        />
      </div>
    </div>
  );
};

export default MemorialDayPlanCard;
